import React from 'react';
import { motion } from 'framer-motion';
import { Bell, BellRing } from 'lucide-react';
import { useFavoriteEvents } from '../../hooks/useFavoriteEvents';
import { useWebPush } from '../../hooks/useWebPush';
import { feedbackEventBell, hapticTap } from '../../utils/appFeedback';

// "Remind me" button for an upcoming live event card. Designed to sit next to
// the relative-time badge in the top-right of the event card so it doesn't
// fight the venue-name pill on the left or the bottom title block.
export function EventFavoriteButton({
  eventId,
  size = 26,
  testIdPrefix = 'event-favorite',
  onChange,
}) {
  const { isFavorite, toggleFavorite } = useFavoriteEvents();
  const { isSupported: pushSupported, isSubscribed, enable: enablePush } = useWebPush();
  const saved = isFavorite(eventId);

  const Icon = saved ? BellRing : Bell;

  const handleClick = (event) => {
    event.stopPropagation();
    event.preventDefault();
    const nextSaved = !saved;
    // Bright bell ping + tap haptic when arming a reminder; just a tap when removing.
    if (nextSaved) feedbackEventBell(); else hapticTap();
    toggleFavorite(eventId);
    onChange?.(nextSaved);
    // First time the user saves an event, ask for push permission so the 2h
    // reminder can actually reach them when the app is closed. Idempotent:
    // a noop on subsequent saves or if push isn't supported.
    if (nextSaved && pushSupported && !isSubscribed) {
      // Fire-and-forget — the OS prompt opens on the same gesture tick.
      enablePush();
    }
  };

  return (
    <motion.button
      type="button"
      data-testid={`${testIdPrefix}-${eventId}`}
      aria-label={saved ? 'Remove reminder' : 'Remind me 2h before'}
      aria-pressed={saved}
      onClick={handleClick}
      whileTap={{ scale: 0.82 }}
      whileHover={{ scale: 1.06 }}
      animate={saved ? { scale: [1, 1.22, 1] } : { scale: 1 }}
      transition={{ duration: 0.28, ease: [0.22, 1.2, 0.36, 1] }}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: saved ? '#FFC93C' : 'rgba(18,18,28,0.62)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        border: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        padding: 0,
        boxShadow: saved
          ? '0 6px 14px rgba(255, 201, 60, 0.45)'
          : '0 4px 10px rgba(0,0,0,0.18)',
      }}
    >
      <Icon
        size={size * 0.55}
        strokeWidth={2.2}
        color={saved ? '#1A1430' : '#FFFFFF'}
        fill={saved ? '#1A1430' : 'transparent'}
      />
    </motion.button>
  );
}
